import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { switchMap } from 'rxjs/operators';
import { CondominiumService } from './condominium.service';

export interface Task {
  id: number;
  createDate: string;
  targetDate: string;
  priority: 'HIGH' | 'NORMAL' | 'LOW';
  status: 'OPEN' | 'CLOSED';
  recurrent: boolean;
  recurrentType?: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  title: string;
  body: string;
  authorName: string;
  condominiumName: string;
}

export interface TaskPage {
  _embedded: {
    taskResponseDTOList: Task[];
  };
  _links: any;
  page: {
    size: number;
    totalElements: number;
    totalPages: number;
    number: number;
  };
}

export interface CreateTaskRequest {
  targetDate: string;
  priority: 'HIGH' | 'NORMAL' | 'LOW';
  recurrent: boolean;
  recurrentType?: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  title: string;
  body: string;
  condominiumId: number;
}

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private condominiumService: CondominiumService
  ) { }

  getTasks(condominiumId: number, page: number = 0, size: number = 5): Observable<TaskPage> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<TaskPage>(`${environment.apiUrl}/tasks`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            condominiumId: condominiumId.toString(),
            page: page.toString(),
            size: size.toString()
          }
        });
      })
    );
  }

  createTask(request: CreateTaskRequest): Observable<Task> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.post<Task>(`${environment.apiUrl}/tasks`, request, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      })
    );
  }

  getTaskDetails(id: number): Observable<Task> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<Task>(`${environment.apiUrl}/tasks/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      })
    );
  }

  updateTaskStatus(id: number, status: string): Observable<Task> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.patch<Task>(`${environment.apiUrl}/tasks/${id}/status`, { status }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      })
    );
  }

  deleteTask(id: number): Observable<void> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.delete<void>(`${environment.apiUrl}/tasks/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      })
    );
  }
}
